import type { ValidIntents } from 'Lib/intent/types';

import type { ProductType } from 'Store/entities/products.types';

export type CRMCampaignInfo = {
  crmCampaignId: string;
  crmCampaignName?: string;
  crmChannel: string;
  crmChannelTemplate: string;
  crmExperimentName?: string;
  crmMessageId?: string;
  crmVariantName?: string;
};

// Merch card
export type ContentBlock = {
  body: string;
  fontSize: number;
  textColorHex: string;
};

export type TextBlockV2 = {
  body: string;
  obsidianAppearance: ObsidianAppearance;
};

export type ObsidianAppearance = {
  fontStyle?: Token;
  foreground?: Token;
  background?: Token;
  border?: Token;
  overlay?: Token;
  size?: string;
  preset?: string;
};

export type Token = {
  name: string;
  type: ObsidianTokenType;
};

export type MediaType = {
  url: string;
  mimeType: string;
  metadata: Record<string, unknown>;
};

export type Media = {
  light: MediaType;
  dark: MediaType;
};

export type Button = {
  tag: number;
  fontSize: number;
  textColorHex: string;
  backgroundColorHex: string;
  copy: string;
  bold: boolean;
  openAction: ValidIntents;
};

export type CTA = {
  body: string;
  action: {
    url?: string;
    interaction: string;
  };
  obsidianAppearance: ObsidianAppearance;
};

export type Action = {
  gestureType: string;
  url: ValidIntents;
};

export type ImageView = {
  backgroundColorHex: string;
  backgroundImage?: string;
  contentType: number;
};

export type HeroImageView = {
  contentType: number;
  heroImage: string;
};

export type Offer = {
  info: {
    productType?: ProductType;
    ruleId: string;
  };
  duration?: {
    body: string;
    fontSize: number;
    textColorHex: string;
  };
};

export type Presentation = {
  rule: string;
  swipeDelay: number;
};

export type CardTemplate = {
  actions: Action[];
  buttons: Button[];
  contentView: ImageView;
  header: ContentBlock;
  heroImageView: HeroImageView;
  message: ContentBlock;
  name: string;
  offer?: Offer;
};

export type InsendioCard = {
  id: number;
  presentation: Presentation;
  template: CardTemplate;
} & CRMCampaignInfo;

// Offer Modal
export type InsendioModal = {
  name: string;
  id: number;
  template: {
    offer: Offer;
    contentView: ImageView;
    heroImageView: HeroImageView;
    backgroundOverlay: {
      backgroundColorHex: string;
      contentType: number;
    };
    header: ContentBlock;
    actions: Action[];
    buttons: Button[];
    message: ContentBlock;
    name: string;
  };
  presentationRule: string;
  presentation: Presentation;
} & CRMCampaignInfo;

export type FullScreenInsendioModalV2 = {
  name: string;
  id: number;
  template: {
    style: number;
    background: ColorToken | { media: Media };
    hero: Media;
    header?: TextBlockV2;
    actions: Action[];
    primaryCta: CTA;
    secondaryCta: CTA;
    body: TextBlockV2[];
    close: ColorToken;
    name: string;
  };
  presentationRule: string;
  presentation: Presentation;
} & CRMCampaignInfo;

export type PartialScreenInsendioModalV2 = {
  name: string;
  id: number;
  template: {
    style: number;
    hero?: Media;
    header: TextBlockV2;
    actions: Action[];
    primaryCta: CTA;
    secondaryCta?: CTA;
    description: TextBlockV2;
    name: string;
  };
  presentationRule: string;
  presentation: Presentation;
} & CRMCampaignInfo;

export type InsendioState = {
  cards: InsendioCard[];
  expiresAt: number;
  modals: (InsendioModal | FullScreenInsendioModalV2 | PartialScreenInsendioModalV2)[];
};

export enum ObsidianTokenType {
  Color = 'TOKEN_TYPE_COLOR',
  Gradient = 'TOKEN_TYPE_GRADIENT',
  Typography = 'TOKEN_TYPE_TYPOGRAPHY',
}

export type ColorToken = {
  color: Token;
};

export type Background = {
  color?: Token;
  media?: Media;
};

export type CampaignData = {
  backgroundColor: string | undefined;
  backgroundImage: string | undefined;
  buttons: Button[];
  header: ContentBlock;
  hero: HeroImageView;
  message: ContentBlock;
  modalType: string;
  offer: Offer | undefined;
  presentationRule: string;
  productType: string;
};

export type FullScreenModalV2CampaignData = {
  backgroundColor: Token | null;
  backgroundImage: Media | null;
  primaryCta: CTA;
  secondaryCta: CTA;
  header: TextBlockV2 | null;
  hero: Media | null;
  body: TextBlockV2[];
  close: Token;
  presentationRule: string;
};

export type PartialScreenModalV2CampaignData = {
  primaryCta: CTA;
  secondaryCta: CTA | null;
  header: TextBlockV2;
  hero: Media | null;
  description: TextBlockV2;
  presentationRule: string;
};

export enum ObsidianButtonSize {
  SIZE_SMALL = 'SIZE_SMALL',
  SIZE_MEDIUM = 'SIZE_MEDIUM',
  SIZE_LARGE = 'SIZE_LARGE',
}

export const OBSIDIAN_BUTTON_MAPPING = {
  [ObsidianButtonSize.SIZE_SMALL]: 'small',
  [ObsidianButtonSize.SIZE_MEDIUM]: 'medium',
  [ObsidianButtonSize.SIZE_LARGE]: 'large',
};
